import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ReactElement } from 'react';

import { t } from '@/shared/i18n';

import { AlertTiersField } from '../AlertTiersField';
import { saveErrorText } from '../api';
import styles from '../notificationsDialog.module.css';
import { alertFeedApi } from './api/alertFeedApi';
import { alertKeys } from './api/queryKeys';
import { useLocationFeedSettings } from './api/useLocationFeedSettings';
import { describeTiers } from './model/describeTiers';

export interface LocationFeedSectionProps {
  location: string;
  locationName: string;
  /** Без сети и в саппорт-сессии: только чтение. */
  disabled: boolean;
}

/**
 * «Лента точки» — пороги ленты и уведомлений Windows на устройствах точки (T-047).
 *
 * Тот же `AlertTiersField`, что у личных порогов (T-046), без копии разметки. Правят
 * менеджер точки и владелец сети: один запрос за раз, без оптимистичного отката,
 * последнюю галочку снять нельзя. Остальные видят одну строку — какой набор действует.
 */
export function LocationFeedSection({
  location,
  locationName,
  disabled,
}: LocationFeedSectionProps): ReactElement | null {
  const client = useQueryClient();
  const settings = useLocationFeedSettings(location);
  const save = useMutation({
    mutationFn: (tiers: string[]) => alertFeedApi.setFeedTiers(location, tiers),
    onSuccess: (data) => client.setQueryData(alertKeys.settings(location), data),
    onError: () => void client.invalidateQueries({ queryKey: alertKeys.settings(location) }),
  });
  const data = settings.data;
  if (!data || !Array.isArray(data.feed_tiers)) return null;
  const title = t('alerts.location.section').replace('{name}', locationName);

  return (
    <section className={styles.body} aria-label={title}>
      <h3 className={styles.sectionTitle}>{title}</h3>
      <p className={styles.muted}>{t('alerts.location.hint')}</p>
      {data.can_edit ? (
        <AlertTiersField
          value={data.feed_tiers}
          available={data.available_tiers}
          disabled={disabled || save.isPending}
          legend={t('alerts.whenLocation')}
          onChange={(tiers) => save.mutate(tiers)}
        />
      ) : (
        <p className={styles.status}>
          {t('alerts.location.readOnly').replace('{tiers}', describeTiers(data.feed_tiers))}
        </p>
      )}
      {save.isError ? (
        <p className={styles.problem} role="alert">
          {saveErrorText(save.error, null)}
        </p>
      ) : null}
    </section>
  );
}
